import { useEffect, useState } from 'react'
import {
  VStack,
  Button,
  Grid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  CheckboxGroup,
  Checkbox,
  Textarea,
  Link,
  useToast,
  Box,
} from '@chakra-ui/react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation, Trans } from 'next-i18next'

import { Turnstile } from '@/features/landing/components/Form/CloudflareTurnstileWidget/Turnstile'

import {
  DEFAULT_TOAST_OPTIONS,
  getErrorToastObject,
} from '@/lib/utils/format/toast'
import { PRIVACY_POLICY, TERMS_OF_USE } from '@/routes'
import requests from '@/lib/requests'
import env from '@/lib/env'
import { useUTMContext } from '@/lib/gtm'

import { SERVICE_OPTIONS } from './constants'
import { schema, FormValue } from './schema'

const defaultValues = {
  firstName: '',
  lastName: '',
  email: '',
  tel: '',
  additionalDetail: '',
  acceptedTerm: false,
  agreedToMarketingConsent: false,
  interestedProducts: [],
  url: '',
}

interface Props {
  onSuccess?: () => void
}

interface FormData {
  firstName: string
  lastName: string
  email: string
  tel: string
  additionalDetail?: string
  acceptedTerm: boolean
  agreedToMarketingConsent?: boolean
  interestedProducts: string[]
}

function ContactForm({ onSuccess }: Props): React.ReactElement {
  const {
    t: tCommon,
    i18n: { language },
  } = useTranslation('common')
  const { t: tOtherServices } = useTranslation('otherServices')

  const toast = useToast()
  const { data: utm } = useUTMContext()

  const {
    register,
    handleSubmit,
    reset: resetForm,
    control,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultValues,
    resolver: zodResolver(schema),
  })

  const [turnstileToken, setTurnstileToken] = useState<string>('');

  const handleVerify = (token: string) => {
    setTurnstileToken(token);
  };

  const onSubmit = async (data: FormValue) => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (isSubmitting) return
    if (!turnstileToken) {
      toast({
        ...DEFAULT_TOAST_OPTIONS,
        status: 'error',
        description: tCommon('errors.missingHumanVerification'),
      });
      return;
    }

    try {
      await requests.post(
        `${env.APP_URL}/api/other-services/contact`,
        { ...data, utm, language, turnstileToken, url },
        {},
      )
      resetForm()
      setTurnstileToken('');

      toast({
        ...DEFAULT_TOAST_OPTIONS,
        description: tOtherServices('contact.form.successToast'),
      })

      onSuccess && onSuccess()
    } catch (error: any) {
      toast(getErrorToastObject(error))
    }
  }

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      toast({
        ...DEFAULT_TOAST_OPTIONS,
        status: 'error',
        description: tCommon('errors.incorrectData'),
      })
    }
  }, [errors])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack
        spacing={{ base: 7, sm: 12, md: 16 }}
        sx={{
          alignItems: 'stretch',
        }}
      >
        <Grid
          sx={{
            gridTemplateColumns: {
              base: '1fr',
              sm: 'repeat(2, 1fr)',
            },
            gridGap: 6,
          }}
        >
          <FormControl isRequired isInvalid={Boolean(errors.firstName)}>
            <FormLabel>
              {tOtherServices('contact.form.firstName.label')}
            </FormLabel>
            <Input
              placeholder={tOtherServices('contact.form.firstName.placeholder')}
              {...register('firstName')}
            />
            {errors.firstName && (
              <FormErrorMessage>{errors.firstName.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl isRequired isInvalid={Boolean(errors.lastName)}>
            <FormLabel>
              {tOtherServices('contact.form.lastName.label')}
            </FormLabel>
            <Input
              placeholder={tOtherServices('contact.form.lastName.placeholder')}
              {...register('lastName')}
            />
            {errors.lastName && (
              <FormErrorMessage>{errors.lastName.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl isRequired isInvalid={Boolean(errors.email)}>
            <FormLabel>{tOtherServices('contact.form.email.label')}</FormLabel>
            <Input
              placeholder={tOtherServices('contact.form.email.placeholder')}
              type="email"
              {...register('email')}
            />
            {errors.email && (
              <FormErrorMessage>{errors.email.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl isRequired isInvalid={Boolean(errors.tel)}>
            <FormLabel>{tOtherServices('contact.form.tel.label')}</FormLabel>
            <Input
              placeholder={tOtherServices('contact.form.tel.placeholder')}
              type="tel"
              {...register('tel')}
            />
            {errors.tel && (
              <FormErrorMessage>{errors.tel.message}</FormErrorMessage>
            )}
          </FormControl>
          <Controller
            name="interestedProducts"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <FormControl
                  isInvalid={Boolean(errors.interestedProducts)}
                  sx={{ gridColumn: { sm: '1 / 3' } }}
                >
                  <FormLabel>
                    {tOtherServices('contact.form.services.label')}
                  </FormLabel>
                  <CheckboxGroup onChange={onChange} value={value}>
                    <Grid
                      sx={{
                        gridTemplateColumns: {
                          base: '1fr',
                          sm: 'repeat(2, 1fr)',
                        },
                        gridGap: '12px 24px',
                        fontWeight: 'medium',
                        justifyItems: 'flex-start',
                      }}
                    >
                      {SERVICE_OPTIONS.map((option, index) => {
                        return (
                          <Checkbox
                            key={`${index}`}
                            value={option}
                            sx={{
                              alignItems: 'flex-start',
                              span: { fontSize: 'md' },
                            }}
                          >
                            {option}
                          </Checkbox>
                        )
                      })}
                    </Grid>
                  </CheckboxGroup>
                  {errors.interestedProducts && (
                    <FormErrorMessage>
                      {errors.interestedProducts.join(', ')}
                    </FormErrorMessage>
                  )}
                </FormControl>
              )
            }}
          />
          <FormControl
            isInvalid={Boolean(errors.additionalDetail)}
            sx={{ gridColumn: { sm: '1 / 3' } }}
          >
            <FormLabel>
              {tOtherServices('contact.form.details.label')}
            </FormLabel>
            <Textarea
              placeholder={tOtherServices('contact.form.details.placeholder')}
              sx={{ height: 28 }}
              {...register('additionalDetail')}
            />
          </FormControl>
          <VStack
            spacing="4"
            sx={{
              alignItems: 'flex-start',
              fontWeight: 'medium',
              gridColumn: { sm: '1 / 3' },
            }}
          >
            <Controller
              name="acceptedTerm"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Checkbox
                    isRequired
                    onChange={onChange}
                    isChecked={value}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <Trans
                      t={tOtherServices}
                      i18nKey="contact.form.agreedToTerms"
                    >
                      ฉันยอมรับ
                      <Link
                        isExternal
                        sx={{ textDecoration: 'underline !important' }}
                        href={`/${language}${TERMS_OF_USE.pathname}`}
                      >
                        เงื่อนไขในการใช้เว็บไซต์
                      </Link>
                      และ
                      <Link
                        isExternal
                        sx={{ textDecoration: 'underline !important' }}
                        href={`/${language}${PRIVACY_POLICY.pathname}`}
                      >
                        นโยบายความเป็นส่วนตัว
                      </Link>
                    </Trans>
                  </Checkbox>
                )
              }}
            />
            <Controller
              name="agreedToMarketingConsent"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Checkbox
                    onChange={onChange}
                    isChecked={value}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <Trans t={tCommon} i18nKey="contact.form.marketingConsent">
                      ฉันต้องการรับโปรโมชันและข่าวสารทางการตลาดเกี่ยวกับ&nbsp;
                      PDPA Core &nbsp;รวมถึงบริการอื่น ๆ ที่เกี่ยวข้องกับ PDPA <br />
                      (คุณสามารถยกเลิกได้ทุกเมื่อ)
                    </Trans>
                  </Checkbox>
                )
              }}
            />
          </VStack>
        </Grid>
        <Box mt={4} width="100%" display="flex" justifyContent="center">
          <Turnstile
            onVerify={handleVerify}
            theme="light"
            action="quotation_form_submit"
          />
        </Box>
        <Button
          isLoading={isSubmitting}
          type="submit"
          size="xl"
          colorScheme="primary"
          sx={{
            alignSelf: 'center',
            w: 'fit-content',
            mx: 'auto',
          }}
        >
          {tOtherServices('contact.form.submitButton')}
        </Button>
      </VStack>
    </form>
  )
}

export default ContactForm
